/**
 * Shapes of the lucide icons (ISC-licensed), as SVG path data. Circles,
 * rects, lines and polygons are converted to the equivalent path so every
 * icon renders as one stroked `<path>`.
 */

/** A shape filled with `currentColor` as well as stroked. */
export interface FilledShape {
  readonly filled: string;
}

export type IconShape = string | FilledShape;

const n = (value: number) => +value.toFixed(4);

/** Path data for an SVG `<circle>`. */
export function circle(cx: number, cy: number, r: number): string {
  return `M${n(cx - r)} ${n(cy)}a${n(r)} ${n(r)} 0 1 0 ${n(2 * r)} 0a${n(r)} ${n(r)} 0 1 0 ${n(-2 * r)} 0Z`;
}

/** Path data for an SVG `<rect>`, with the same corner clamping as SVG. */
export function rect(
  x: number,
  y: number,
  width: number,
  height: number,
  rx?: number,
  ry?: number,
): string {
  const cornerX = Math.min(rx ?? ry ?? 0, width / 2);
  const cornerY = Math.min(ry ?? rx ?? 0, height / 2);
  if (cornerX === 0 || cornerY === 0) {
    return `M${n(x)} ${n(y)}h${n(width)}v${n(height)}h${n(-width)}Z`;
  }
  const arc = (dx: number, dy: number) =>
    `a${n(cornerX)} ${n(cornerY)} 0 0 1 ${n(dx)} ${n(dy)}`;
  return [
    `M${n(x + cornerX)} ${n(y)}`,
    `h${n(width - 2 * cornerX)}`,
    arc(cornerX, cornerY),
    `v${n(height - 2 * cornerY)}`,
    arc(-cornerX, cornerY),
    `h${n(-(width - 2 * cornerX))}`,
    arc(-cornerX, -cornerY),
    `v${n(-(height - 2 * cornerY))}`,
    arc(cornerX, -cornerY),
    'Z',
  ].join('');
}

/** Path data for an SVG `<line>`. */
export function line(x1: number, y1: number, x2: number, y2: number): string {
  return `M${n(x1)} ${n(y1)}L${n(x2)} ${n(y2)}`;
}

/** Path data for an SVG `<polyline>`. */
export function polyline(points: string): string {
  return `M${points}`;
}

/** Path data for an SVG `<polygon>`. */
export function polygon(points: string): string {
  return `M${points}Z`;
}

/** Marks a shape as filled with `currentColor`. */
export function filled(shape: string): FilledShape {
  return { filled: shape };
}

export const ICON_SHAPES = {
  'alert-triangle': [
    'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3',
    'M12 9v4',
    'M12 17h.01',
  ],
  'arrow-right': ['M5 12h14', 'm12 5 7 7-7 7'],
  bath: [
    'M10 4 8 6',
    'M17 19v2',
    'M2 12h20',
    'M7 19v2',
    'M9 5 7.621 3.621A2.121 2.121 0 0 0 4 5v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5',
  ],
  'bed-double': [
    'M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8',
    'M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4',
    'M12 4v6',
    'M2 18h20',
  ],
  'calendar-check': [
    'M8 2v4',
    'M16 2v4',
    rect(3, 4, 18, 18, 2),
    'M3 10h18',
    'm9 16 2 2 4-4',
  ],
  check: ['M20 6 9 17l-5-5'],
  'check-circle-2': [circle(12, 12, 10), 'm9 12 2 2 4-4'],
  clock: [circle(12, 12, 10), 'M12 6v6l4 2'],
  cog: [
    'M11 10.27 7 3.34',
    'm11 13.73-4 6.93',
    'M12 22v-2',
    'M12 2v2',
    'M14 12h8',
    'm17 20.66-1-1.73',
    'm17 3.34-1 1.73',
    'M2 12h2',
    'm20.66 17-1.73-1',
    'm20.66 7-1.73 1',
    'm3.34 17 1.73-1',
    'm3.34 7 1.73 1',
    circle(12, 12, 2),
    circle(12, 12, 8),
  ],
  'dollar-sign': [
    line(12, 2, 12, 22),
    'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  ],
  'file-check-2': [
    'M10.5 22H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.706.706l3.588 3.588A2.4 2.4 0 0 1 20 8v6',
    'M14 2v5a1 1 0 0 0 1 1h5',
    'm14 20 2 2 4-4',
  ],
  'file-signature': [
    'M14.364 13.634a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506l4.013-4.009a1 1 0 0 0-3.004-3.004z',
    'M14.487 7.858A1 1 0 0 1 14 7V2',
    'M20 19.645V20a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l2.516 2.516',
    'M8 18h1',
  ],
  handshake: [
    'm11 17 2 2a1 1 0 1 0 3-3',
    'm14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4',
    'm21 3 1 11h-2',
    'M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3',
    'M3 4h8',
  ],
  'map-pin': [
    'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
    circle(12, 10, 3),
  ],
  megaphone: [
    'M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z',
    'M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14',
    'M8 6v8',
  ],
  quote: [
    'M16 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z',
    'M5 3a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2 1 1 0 0 1 1 1v1a2 2 0 0 1-2 2 1 1 0 0 0-1 1v2a1 1 0 0 0 1 1 6 6 0 0 0 6-6V5a2 2 0 0 0-2-2z',
  ],
  repeat: [
    'm17 2 4 4-4 4',
    'M3 11v-1a4 4 0 0 1 4-4h14',
    'm7 22-4-4 4-4',
    'M21 13v1a4 4 0 0 1-4 4H3',
  ],
  'shield-check': [
    'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z',
    'm9 12 2 2 4-4',
  ],
  sparkles: [
    'M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z',
    'M20 2v4',
    'M22 4h-4',
    circle(4, 20, 2),
  ],
  square: [rect(3, 3, 18, 18, 2)],
  star: [
    'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z',
  ],
  users: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M16 3.128a4 4 0 0 1 0 7.744',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    circle(9, 7, 4),
  ],
  'users-2': [
    'M18 21a8 8 0 0 0-16 0',
    circle(10, 8, 5),
    'M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3',
  ],
  x: ['M18 6 6 18', 'm6 6 12 12'],
  zap: [
    'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z',
  ],
  'arrow-left': ['m12 19-7-7 7-7', 'M19 12H5'],
  phone: [
    'M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384',
  ],
  menu: ['M4 5h16', 'M4 12h16', 'M4 19h16'],
  brain: [
    'M12 18V5',
    'M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4',
    'M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5',
    'M17.997 5.125a4 4 0 0 1 2.526 5.77',
    'M18 18a4 4 0 0 0 2-7.464',
    'M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517',
    'M6 18a4 4 0 0 1-2-7.464',
    'M6.003 5.125a4 4 0 0 0-2.526 5.77',
  ],
  'message-square': [
    'M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z',
  ],
  heart: [
    'M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5',
  ],
  search: ['m21 21-4.34-4.34', circle(11, 11, 8)],
  'trending-up': ['M16 7h6v6', 'm22 7-8.5 8.5-5-5L2 17'],
  bot: [
    'M12 8V4H8',
    rect(4, 8, 16, 12, 2),
    'M2 14h2',
    'M20 14h2',
    'M15 13v2',
    'M9 13v2',
  ],
  briefcase: [
    'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16',
    rect(2, 6, 20, 14, 2),
  ],
  trophy: [
    'M10 14.66v1.626a2 2 0 0 1-.976 1.696A5 5 0 0 0 7 21.978',
    'M14 14.66v1.626a2 2 0 0 0 .976 1.696A5 5 0 0 1 17 21.978',
    'M18 9h1.5a1 1 0 0 0 0-5H18',
    'M4 22h16',
    'M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z',
    'M6 9H4.5a1 1 0 0 1 0-5H6',
  ],
  'x-circle': [circle(12, 12, 10), 'm15 9-6 6', 'm9 9 6 6'],
  calendar: ['M8 2v4', 'M16 2v4', rect(3, 4, 18, 18, 2), 'M3 10h18'],
  'building-2': [
    'M10 12h4',
    'M10 8h4',
    'M14 21v-3a2 2 0 0 0-4 0v3',
    'M6 10H4a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2',
    'M6 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16',
  ],
  wrench: [
    'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z',
  ],
  smartphone: [rect(5, 2, 14, 20, 2, 2), 'M12 18h.01'],
  home: [
    'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8',
    'M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  ],
  'key-round': [
    'M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z',
    filled(circle(16.5, 7.5, 0.5)),
  ],
  layers: [
    'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
    'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12',
    'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17',
  ],
  plus: ['M5 12h14', 'M12 5v14'],
  'bar-chart-3': [
    'M3 3v16a2 2 0 0 0 2 2h16',
    'M18 17V9',
    'M13 17V5',
    'M8 17v-3',
  ],
  wallet: [
    'M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1',
    'M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4',
  ],
  camera: [
    'M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z',
    circle(12, 13, 3),
  ],
  'clipboard-check': [
    rect(8, 2, 8, 4, 1, 1),
    'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2',
    'm9 14 2 2 4-4',
  ],
  gavel: [
    'm14 13-8.381 8.38a1 1 0 0 1-3.001-3l8.384-8.381',
    'm16 16 6-6',
    'm21.5 10.5-8-8',
    'm8 8 6-6',
    'm8.5 7.5 8 8',
  ],
  bell: [
    'M10.268 21a2 2 0 0 0 3.464 0',
    'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
  ],
  'bell-ring': [
    'M10.268 21a2 2 0 0 0 3.464 0',
    'M22 8c0-2.3-.8-4.3-2-6',
    'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
    'M4 2C2.8 3.7 2 5.7 2 8',
  ],
  'credit-card': [rect(2, 5, 20, 14, 2), line(2, 10, 22, 10)],
  'file-text': [
    'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z',
    'M14 2v5a1 1 0 0 0 1 1h5',
    'M10 9H8',
    'M16 13H8',
    'M16 17H8',
  ],
  mail: ['m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7', rect(2, 4, 20, 16, 2)],
  'file-warning': [
    'M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z',
    'M12 9v4',
    'M12 17h.01',
  ],
  'piggy-bank': [
    'M11 17h3v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3a3.16 3.16 0 0 0 2-2h1a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1h-1a5 5 0 0 0-2-4V3a4 4 0 0 0-3.2 1.6l-.3.4H11a6 6 0 0 0-6 6v1a5 5 0 0 0 2 4v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1z',
    'M16 10h.01',
    'M2 8v1a2 2 0 0 0 2 2h1',
  ],
  truck: [
    'M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2',
    'M15 18H9',
    'M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 18.52 8H14',
    circle(17, 18, 2),
    circle(7, 18, 2),
  ],
  wifi: [
    'M12 20h.01',
    'M2 8.82a15 15 0 0 1 20 0',
    'M5 12.859a10 10 0 0 1 14 0',
    'M8.5 16.429a5 5 0 0 1 7 0',
  ],
  lock: [rect(3, 11, 18, 11, 2, 2), 'M7 11V7a5 5 0 0 1 10 0v4'],
  'badge-check': [
    'M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z',
    'm9 12 2 2 4-4',
  ],
  dishwasher: [
    rect(3, 3, 18, 18, 2),
    'M3 9h18',
    'M6 6h.01',
    'M9 6h.01',
    circle(12, 14, 4),
    'M12 12v2',
  ],
  parking: [rect(3, 3, 18, 18, 2), 'M9 17V7h4a3 3 0 0 1 0 6H9'],
  outdoor: ['M12 19V5', 'm5 12 7-7 7 7', 'm4 19 8-7 8 7', 'M5 21h14'],
  'washing-machine': [
    rect(3, 3, 18, 18, 2),
    circle(12, 13, 5),
    'M12 10a3 3 0 0 1 3 3',
    filled(circle(7, 6, 1)),
    filled(circle(10, 6, 1)),
  ],
  inbox: [
    polyline('22 12 16 12 14 15 10 15 8 12 2 12'),
    'M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  ],
  eye: ['M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z', circle(12, 12, 3)],
  pencil: [
    'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
    'm15 5 4 4',
  ],
  'refresh-cw': [
    'M3 12a9 9 0 0 1 15-6.7L21 8',
    'M21 3v5h-5',
    'M21 12a9 9 0 0 1-15 6.7L3 16',
    'M3 21v-5h5',
  ],
  download: [
    'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4',
    polyline('7 10 12 15 17 10'),
    line(12, 15, 12, 3),
  ],
  ruler: [
    'M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z',
    'm14.5 12.5 2-2',
    'm11.5 9.5 2-2',
    'm8.5 6.5 2-2',
    'm17.5 15.5 2-2',
  ],
  'chevron-down': ['m6 9 6 6 6-6'],
  'chevron-left': ['m15 18-6-6 6-6'],
  'chevron-right': ['m9 18 6-6-6-6'],
  'circle-help': [
    circle(12, 12, 10),
    'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3',
    'M12 17h.01',
  ],
  image: [
    rect(3, 3, 18, 18, 2, 2),
    circle(9, 9, 2),
    'm21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21',
  ],
  map: [
    'M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z',
    'M15 5.764v15',
    'M9 3.236v15',
  ],
  paintbrush: [
    'm14.622 17.897-10.68-2.913',
    'M18.376 2.622a1 1 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0z',
    'M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15',
  ],
  'paw-print': [
    circle(11, 4, 2),
    circle(18, 8, 2),
    circle(20, 16, 2),
    'M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z',
  ],
  play: [polygon('6 3 20 12 6 21 6 3')],
  'share-2': [
    circle(18, 5, 3),
    circle(6, 12, 3),
    circle(18, 19, 3),
    line(8.59, 13.51, 15.42, 17.49),
    line(15.41, 6.51, 8.59, 10.49),
  ],
  'arrow-up-down': ['m21 16-4 4-4-4', 'M17 20V4', 'm3 8 4-4 4 4', 'M7 4v16'],
  'arrow-up-right': ['M7 7h10v10', 'M7 17 17 7'],
  'calendar-days': [
    'M8 2v4',
    'M16 2v4',
    rect(3, 4, 18, 18, 2),
    'M3 10h18',
    'M8 14h.01',
    'M12 14h.01',
    'M16 14h.01',
    'M8 18h.01',
    'M12 18h.01',
    'M16 18h.01',
  ],
  'car-front': [
    'm21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8',
    'M7 14h.01',
    'M17 14h.01',
    rect(3, 10, 18, 8, 2),
    'M5 18v2',
    'M19 18v2',
  ],
  'circle-check': [circle(12, 12, 10), 'm9 12 2 2 4-4'],
  images: [
    'm22 11-1.296-1.296a2.4 2.4 0 0 0-3.408 0L11 16',
    'M4 8a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2',
    filled(circle(13, 7, 1)),
    rect(8, 2, 14, 14, 2),
  ],
  'maximize-2': ['M15 3h6v6', 'm21 3-7 7', 'm3 21 7-7', 'M9 21H3v-6'],
  'message-circle': [
    'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719',
  ],
  minus: ['M5 12h14'],
  trees: [
    'M10 10v.2A3 3 0 0 1 8.9 16H5a3 3 0 0 1-1-5.8V10a3 3 0 0 1 6 0Z',
    'M7 16v6',
    'M13 19v3',
    'M12 19h8.3a1 1 0 0 0 .7-1.7L18 14h.3a1 1 0 0 0 .7-1.7L16 9h.2a1 1 0 0 0 .8-1.7L13 3l-1.4 1.5',
  ],
  'concierge-bell': [
    'M3 20a1 1 0 0 1-1-1v-1a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v1a1 1 0 0 1-1 1Z',
    'M20 16a8 8 0 1 0-16 0',
    'M12 4v4',
    'M10 4h4',
  ],
  dumbbell: [
    'm6.5 6.5 11 11',
    'm21 21-1-1',
    'm3 3 1 1',
    'm18 22 4-4',
    'm2 6 4-4',
    'm3 10 7-7',
    'm14 21 7-7',
  ],
  flame: [
    'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z',
  ],
  'move-vertical': ['M12 2v20', 'm8 18 4 4 4-4', 'm8 6 4-4 4 4'],
  'rows-3': [rect(3, 3, 18, 18, 2), 'M21 9H3', 'M21 15H3'],
  snowflake: [
    'M2 12h20',
    'M12 2v20',
    'm20 16-4-4 4-4',
    'm4 8 4 4-4 4',
    'm16 4-4 4-4-4',
    'm8 20 4-4 4 4',
  ],
  waves: [
    'M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
    'M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
    'M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
  ],
  utensils: [
    'M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2',
    'M7 2v20',
    'M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7',
  ],
  'layout-grid': [
    rect(3, 3, 7, 7, 1),
    rect(14, 3, 7, 7, 1),
    rect(14, 14, 7, 7, 1),
    rect(3, 14, 7, 7, 1),
  ],
  'sliders-horizontal': [
    'M21 4h-7',
    'M10 4H3',
    'M21 12h-9',
    'M8 12H3',
    'M21 20h-5',
    'M12 20H3',
    'M14 2v4',
    'M8 10v4',
    'M16 18v4',
  ],
} as const satisfies Record<string, readonly IconShape[]>;

export type IconName = keyof typeof ICON_SHAPES;
